/**
 * TopicsNavDropdown — desktop nav popover that lists every SEO topic
 * hub (`/topics/:slug`) so users can discover them without knowing the
 * URLs. Fetched from `/api/topics` on mount and cached in module-scope
 * across route changes so the dropdown opens instantly on hover.
 */
import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Compass, ChevronDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { AuthContext } from '../App';

let TOPICS_CACHE = null; // populated on first fetch; kept for the tab session

const TopicsNavDropdown = () => {
  const { API } = useContext(AuthContext);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [topics, setTopics] = useState(TOPICS_CACHE || []);

  useEffect(() => {
    if (TOPICS_CACHE) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/topics`);
        if (!cancelled && Array.isArray(data)) {
          TOPICS_CACHE = data;
          setTopics(data);
        }
      } catch { /* silently soft-fail — nav still works */ }
    })();
    return () => { cancelled = true; };
  }, [API]);

  const active = pathname.startsWith('/topics');

  if (!topics || topics.length === 0) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-haspopup="true"
          aria-expanded={open}
          data-testid="nav-topics-trigger"
          className={`inline-flex items-center gap-1.5 rounded-full px-3.5 h-9 text-sm font-semibold transition-colors flex-shrink-0
            ${active
              ? 'bg-primary/10 text-primary'
              : 'text-foreground/75 hover:text-foreground hover:bg-muted'
            }`}
        >
          <Compass className="w-4 h-4" />
          <span>Topics</span>
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={8} className="w-[420px] p-3" data-testid="nav-topics-menu">
        <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-muted-foreground px-2 pt-1 pb-2">
          Explore topic hubs
        </div>
        <div className="grid grid-cols-1 gap-1">
          {topics.map((t) => {
            const isActive = pathname === `/topics/${t.slug}`;
            return (
              <button
                key={t.slug}
                type="button"
                onClick={() => { setOpen(false); navigate(`/topics/${t.slug}`); }}
                data-testid={`nav-topics-${t.slug}`}
                className={`flex items-start gap-3 p-2.5 rounded-xl text-left transition-colors
                  ${isActive ? 'bg-primary/10' : 'hover:bg-muted'}`}
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0
                  ${isActive ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground/70'}`}>
                  <Compass className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <div className={`text-sm font-bold ${isActive ? 'text-primary' : 'text-foreground'}`}>
                    {t.title}
                  </div>
                  {t.description && (
                    <div className="text-[11px] text-muted-foreground line-clamp-2">
                      {t.description}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default TopicsNavDropdown;
